import React, { useState, useEffect, useCallback } from "react";
import { pickBy, isEmpty, map } from "lodash";
import { withDatabase } from "@nozbe/watermelondb/DatabaseProvider";
import withObservables from "@nozbe/with-observables";
import { Q } from "@nozbe/watermelondb";
import { ConfigContext } from "../containers/SiteConfig";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@material-ui/core";
import { ProgramRowItem } from "./ProgramRowItem";
import ProgramTableHeader from "./ProgramTableHeader";
import ProgramListMeta from "./ProgramsListMeta";

import { useStyles, stableSort, getSorting } from "../utils";
import { getProgramsQuery, filterPrograms } from "../db/queries/programs";
import { noDataMessage } from "../constants";
import { sendMessage } from "../iframeConnect";
import Loader from "../containers/Loader";

const ProgramsTable = (props) => {
  const {
    programs: programsList,
    filters,
    handleDelete,
    searchText,
    setSearchText,
    handleClearFilters,
    programLink,
    defaultSort,
    defaultSortDirection,
  } = props;
  const { searchZipCode, searchRadius, showZipCodeFinder, setMarkers } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState([]);

  const { columnSettings = [], firstDayOfTheWeek } =
    React.useContext(ConfigContext);
  const totalColumns = columnSettings.length + 1;

  const classes = useStyles();
  const [order, setOrder] = useState(defaultSortDirection);
  const [orderBy, setOrderBy] = useState(defaultSort);

  useEffect(() => {
    if (showZipCodeFinder === "yes") {
      const filteredPrograms = filterPrograms(
        programsList,
        searchZipCode,
        searchRadius
      );
      setPrograms(filteredPrograms);
      setIsLoading(false);
      setMarkers(filteredPrograms);
    } else {
      setPrograms(programsList);
      setIsLoading(false);
    }

    sendMessage("contentUpdate", undefined, true); // hide means don't scroll
    sendMessage("scrollIframe", undefined, undefined, undefined);
  }, [
    programsList.length,
    searchZipCode,
    searchRadius,
    showZipCodeFinder,
    programsList,
    setMarkers,
  ]);

  const handleRequestSort = (event, property) => {
    if (orderBy === property) {
      const isDesc = order === "desc";
      setOrder(isDesc ? "asc" : "desc");
    } else {
      setOrder("asc");
      setOrderBy(property);
    }
  };

  return (
    <div style={{ top: "20px", position: "relative" }}>
      <ProgramListMeta
        programs={programs}
        searchText={searchText}
        filters={filters}
        handleDelete={handleDelete}
        setSearchText={setSearchText}
        handleClearFilters={handleClearFilters}
        programLink={programLink}
      />
      <Paper className={classes.programsListTable}>
        <Table>
          <ProgramTableHeader
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <Loader showLoading={isLoading}>
            <TableBody>
              {stableSort(
                programs,
                getSorting(order, orderBy, firstDayOfTheWeek),
                getSorting("asc", "startTime")
              ).map((program, index) => {
                return (
                  <ProgramRowItem
                    key={program.programId}
                    program={program}
                    totalColumns={totalColumns}
                  />
                );
              })}
              {isEmpty(programs) ? (
                <TableRow>
                  <TableCell
                    colSpan={totalColumns}
                    key="message"
                    style={{ paddingTop: 0 }}
                  >
                    <center>
                      {" "}
                      <h3>{noDataMessage}</h3>{" "}
                    </center>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Loader>
        </Table>
      </Paper>
    </div>
  );
};

const enhance = withObservables(
  ["filters", "searchText", "sitesInfo"],
  ({
    database,
    filters,
    searchText,
    searchZipCode,
    searchRadius,
    showZipCodeFinder,
    sitesInfo,
  }) => {
    if (
      showZipCodeFinder === "yes" &&
      isEmpty(searchText) &&
      isEmpty(searchZipCode) &&
      isEmpty(searchRadius)
    ) {
      return {
        programs: [],
      };
    }

    const selectedFilters = pickBy(filters, (value, key) => !isEmpty(value));
    const siteIds = map(sitesInfo, ({ siteId }) => parseInt(siteId));
    const query = getProgramsQuery(
      selectedFilters,
      siteIds,
      searchText,
      searchZipCode,
      searchRadius
    );
    const data = database.collections
      .get("programs")
      .query(...query)
      .observe();

    return {
      programs: data,
    };
  }
);

export default withDatabase(enhance(ProgramsTable));
